import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Minus, Plus, ChevronDown, ChevronUp, MessageSquare, X } from 'lucide-react';
import { getYieldHint } from '@/lib/productHelpers';

export const CountEntryRow = React.memo(function CountEntryRow({
  line,
  product,
  localCount,
  onCountChange,
  onAdvance,
  flushRegistry,
}) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(localCount?.notes || '');
  const [inputValue, setInputValue] = useState(() =>
    localCount?.quantity != null ? String(localCount.quantity) : ''
  );
  // iter365 — Splits are a UI-only breakdown of the row's quantity across
  // multiple shelves / locations. When non-empty, the qty input is
  // replaced by N split inputs and the SUM drives `propagate`. Splits
  // are persisted via `saveLocalCount` so the tablet doesn't lose them
  // on reload. Initial state is hydrated from localCount.splits (if any).
  const [splits, setSplits] = useState(() => {
    if (Array.isArray(localCount?.splits) && localCount.splits.length > 0) {
      return localCount.splits.map((s, i) => ({
        qty: s.qty != null ? String(s.qty) : '',
        // iter374 — per-split unit override. Defaults to row's primary
        // unit when blank; an explicit unit_id triggers conversion via
        // conversion_to_base when summing.
        unit_id: s.unit_id ?? null,
        _key: s._key || `s${i}-${Date.now()}`,
      }));
    }
    return [];
  });
  const [savedFlash, setSavedFlash] = useState(false);

  const debounceRef = useRef(null);
  const flashTimerRef = useRef(null);
  const inputValueRef = useRef(inputValue);
  const notesRef = useRef(notes);
  const splitsRef = useRef(splits);

  // Keep refs in sync (no extra renders)
  inputValueRef.current = inputValue;
  notesRef.current = notes;
  splitsRef.current = splits;

  const unitLabel = localCount?.unit_label || line.unit_label_snapshot || '';
  // Filter the unit list to only show units that are valid for CLOSING-STOCK
  // counting: base stock units, explicit count units, or the unit the count
  // sheet was originally configured with (unit_label_snapshot).
  // Pure sales units (e.g. shot_25ml when only is_sales_unit=true) are
  // excluded from the count dropdown so an auditor cannot accidentally record
  // "4.1 shots" when they mean "4.1 bottles", which would cause a ×28 under-
  // valuation in the calculation engine.
  const units = useMemo(() => {
    const all = product?.units || [];
    const snapLabel = (line.unit_label_snapshot || '').toLowerCase();
    const countable = all.filter(u =>
      u.is_base_stock_unit ||
      u.is_default_count_unit ||
      (u.unit_label || '').toLowerCase() === snapLabel
    );
    // Safety net: if all units were filtered out, fall back to the full list
    // so the UI never loses the ability to record a count.
    return countable.length > 0 ? countable : all;
  }, [product?.units, line.unit_label_snapshot]);
  const hasMultipleUnits = units.length > 1;

  // Derive default unit_id from the label (ensures unit_id and unit_label stay in sync)
  const unitId = useMemo(() => {
    if (localCount?.unit_id) return localCount.unit_id;
    // Match the label to a product unit to get the correct ID
    const label = (localCount?.unit_label || line.unit_label_snapshot || '').toLowerCase();
    if (label && units.length > 0) {
      const match = units.find(u => (u.unit_label || '').toLowerCase() === label);
      if (match) return match.id;
    }
    return units[0]?.id ?? null;
  }, [localCount?.unit_id, localCount?.unit_label, line.unit_label_snapshot, units]);

  // Sync input from external changes (e.g., after re-download from server)
  useEffect(() => {
    const externalQty = localCount?.quantity;
    const currentParsed = inputValueRef.current === '' ? null : parseFloat(inputValueRef.current);
    if (externalQty !== currentParsed) {
      setInputValue(externalQty != null ? String(externalQty) : '');
    }
  }, [localCount?.quantity]);

  useEffect(() => {
    if (localCount?.notes !== undefined) {
      setNotes(localCount.notes || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localCount?.notes]);

  // Flash saved indicator
  const flashSaved = useCallback(() => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setSavedFlash(true);
    flashTimerRef.current = setTimeout(() => setSavedFlash(false), 600);
  }, []);

  // Propagate current values to parent (triggers IndexedDB save)
  const propagate = useCallback((qtyStr, opts = {}) => {
    const val = qtyStr === '' ? null : parseFloat(qtyStr);
    if (val !== null && isNaN(val)) return;
    onCountChange(line.id, {
      quantity: val,
      unit_id: opts.unitId ?? unitId ?? (units[0]?.id ?? null),
      unit_label: opts.unitLabel ?? unitLabel ?? (units[0]?.unit_label ?? ''),
      notes: opts.notes ?? notesRef.current,
      // iter365 — Persist splits whenever they're active. Cleared (empty
      // array) when the row collapses back to a single qty input.
      splits: opts.splits !== undefined ? opts.splits : splitsRef.current,
    });
    flashSaved();
  }, [line.id, unitId, unitLabel, units, onCountChange, flashSaved]);

  // Schedule debounced propagation (300ms after last keystroke)
  const schedulePropagate = useCallback((qtyStr) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      propagate(qtyStr);
    }, 300);
  }, [propagate]);

  // Flush: immediately propagate any pending debounce
  const flush = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      propagate(inputValueRef.current);
    }
  }, [propagate]);

  // Register/deregister flush callback with parent's registry
  useEffect(() => {
    if (!flushRegistry) return;
    const reg = flushRegistry;
    reg.current.add(flush);
    return () => reg.current.delete(flush);
  }, [flush, flushRegistry]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  // --- Input handlers ---

  const handleInputChange = useCallback((e) => {
    const raw = e.target.value;
    setInputValue(raw);
    schedulePropagate(raw);
  }, [schedulePropagate]);

  const handleBlur = useCallback(() => {
    flush();
  }, [flush]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      flush();
      if (onAdvance) onAdvance(line.id);
    }
  }, [flush, onAdvance, line.id]);

  const handleIncrement = useCallback(() => {
    const current = inputValueRef.current === '' ? 0 : (parseFloat(inputValueRef.current) || 0);
    const next = String(current + 1);
    setInputValue(next);
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    propagate(next);
  }, [propagate]);

  const handleDecrement = useCallback(() => {
    const current = inputValueRef.current === '' ? 0 : (parseFloat(inputValueRef.current) || 0);
    if (current > 0) {
      const next = String(current - 1);
      setInputValue(next);
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
      propagate(next);
    }
  }, [propagate]);

  const handleUnitChange = useCallback((e) => {
    const selectedUnitId = parseInt(e.target.value, 10);
    const unit = units.find(u => u.id === selectedUnitId);
    // Cancel any pending debounce (don't flush with old unit_id — that causes a race)
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    propagate(inputValueRef.current, {
      unitId: selectedUnitId,
      unitLabel: unit?.unit_label || unitLabel,
    });
  }, [units, unitLabel, propagate]);

  const handleNotesBlur = useCallback(() => {
    if (notesRef.current !== (localCount?.notes || '')) {
      propagate(inputValueRef.current, { notes: notesRef.current });
    }
  }, [localCount?.notes, propagate]);

  // iter365 — Split helpers. Splits live in component state; their sum
  // drives the persisted quantity via `propagate(sumStr, { splits })`.
  // iter374 — Unit-aware sum. When a split overrides `unit_id`, its qty
  // is converted into the row's primary unit using `conversion_to_base`
  // (qty_target = qty × src.conv_to_base / target.conv_to_base). When
  // the override is missing or conversion data is incomplete, the qty
  // is added directly (safe fallback — matches prior behaviour).
  const sumSplits = useCallback((arr) => {
    const targetUnit = units.find(u => u.id === unitId) || units[0] || null;
    const targetConv = targetUnit ? (parseFloat(targetUnit.conversion_to_base) || 1) : 1;
    return (arr || []).reduce((s, x) => {
      const q = parseFloat(x.qty) || 0;
      if (q === 0) return s;
      const srcUnit = (x.unit_id && units.find(u => u.id === x.unit_id)) || targetUnit;
      const srcConv = srcUnit ? (parseFloat(srcUnit.conversion_to_base) || 1) : 1;
      if (!srcUnit || !targetUnit || srcUnit.id === targetUnit.id) return s + q;
      return s + (q * srcConv / targetConv);
    }, 0);
  }, [units, unitId]);

  // iter384 — Q6.b. When a split overrides to a measure whose
  // `conversion_to_base` is missing/zero on this product, sumSplits
  // silently falls back to 1:1 addition. Surface that to the auditor
  // so they can review the count rather than trust a bad conversion.
  const splitsNeedReview = useCallback(() => {
    if (!splits || splits.length === 0) return false;
    return splits.some(s => {
      if (!s.unit_id) return false;
      const u = units.find(uu => uu.id === s.unit_id);
      const conv = u ? parseFloat(u.conversion_to_base) : null;
      return !u || !conv || conv <= 0;
    });
  }, [splits, units]);

  const updateSplitUnit = useCallback((idx, newUnitId) => {
    const prev = splitsRef.current;
    const parsed = newUnitId ? parseInt(newUnitId, 10) : null;
    const next = prev.map((s, i) => (i === idx ? { ...s, unit_id: parsed } : s));
    const sum = sumSplits(next);
    const sumStr = sum > 0 ? String(Math.round(sum * 100) / 100) : '';
    setSplits(next);
    setInputValue(sumStr);
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    propagate(sumStr, { splits: next });
  }, [sumSplits, propagate]);

  const initSplits = useCallback(() => {
    // First click on "+ split count" — seed splits[0] with the current
    // qty (if any) and append an empty splits[1] so the auditor sees
    // both their existing number and an empty slot to add to.
    const cur = splitsRef.current;
    if (cur && cur.length > 0) {
      // Add one more split
      const next = [...cur, { qty: '', _key: `s${cur.length}-${Date.now()}` }];
      setSplits(next);
      const sum = sumSplits(next);
      const sumStr = sum > 0 ? String(sum) : '';
      setInputValue(sumStr);
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
      // Only propagate if the sum actually has a value; adding an empty
      // slot to an already-empty split set produces no data change and
      // would push a null qty to the server on the next autosync.
      if (sumStr !== '') propagate(sumStr, { splits: next });
      return;
    }
    const currentQty = inputValueRef.current;
    const seeded = [
      { qty: currentQty === '' ? '' : currentQty, _key: `s0-${Date.now()}` },
      { qty: '', _key: `s1-${Date.now()}` },
    ];
    setSplits(seeded);
    // Only propagate if there is already a counted quantity. Clicking
    // "+ split count" on an empty row is a UI-only action — it does not
    // change the stored quantity, so there is nothing to autosave yet.
    if (currentQty !== '') propagate(currentQty, { splits: seeded });
  }, [propagate, sumSplits]);

  const updateSplit = useCallback((idx, val) => {
    // iter365 — Compute next splits + sum OUTSIDE setSplits so we don't
    // call setInputValue inside a state updater (React would warn about
    // "setState during render"). Read fresh state from splitsRef.
    const prev = splitsRef.current;
    const next = prev.map((s, i) => (i === idx ? { ...s, qty: val } : s));
    const sum = sumSplits(next);
    // iter374 — round to 2 dp because unit conversion can yield decimals
    const sumStr = sum > 0 ? String(Math.round(sum * 100) / 100) : '';
    setSplits(next);
    setInputValue(sumStr);
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      propagate(sumStr, { splits: next });
    }, 300);
  }, [propagate, sumSplits]);

  const removeSplit = useCallback((idx) => {
    const prev = splitsRef.current;
    if (prev.length <= 1) {
      // Collapse back to single-input mode entirely.
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
      setSplits([]);
      // Only propagate if the main input already had a qty; removing the last
      // empty split from an uncounted row produces no data change.
      if (inputValueRef.current !== '') propagate(inputValueRef.current, { splits: [] });
      return;
    }
    const filtered = prev.filter((_, i) => i !== idx);
    if (filtered.length === 1) {
      // Only one split left — promote its qty into the main input and
      // clear splits mode.
      const remaining = filtered[0].qty || '';
      setSplits([]);
      setInputValue(remaining);
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
      // Only propagate if there is an actual qty to save.
      if (remaining !== '') propagate(remaining, { splits: [] });
      return;
    }
    const sum = sumSplits(filtered);
    const sumStr = sum > 0 ? String(sum) : '';
    setSplits(filtered);
    setInputValue(sumStr);
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    if (sumStr !== '') propagate(sumStr, { splits: filtered });
  }, [propagate, sumSplits]);

  const splitsActive = splits.length > 0;
  const isCounted = inputValue !== '';
  const quantity = inputValue === '' ? null : parseFloat(inputValue);

  return (
    <div
      className={`border-b border-slate-100 last:border-b-0 transition-colors duration-500 ${
        savedFlash ? 'bg-emerald-100' : isCounted ? 'bg-green-50/40' : 'bg-white'
      }`}
      data-testid={`count-entry-row-${line.id}`}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Product info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate" data-testid={`count-product-name-${line.id}`}>
            {product?.product_code ? <span className="text-slate-400 font-normal">{product.product_code}</span> : null}
            {product?.product_code ? ' ' : ''}{line.product_name_snapshot}
          </p>
          {line.location_name_snapshot && (
            <p className="text-xs text-slate-400 truncate">{line.location_name_snapshot}</p>
          )}
          {(() => {
            const yh = getYieldHint(product);
            return yh ? <p className="text-xs text-purple-600 font-medium truncate" data-testid={`count-yield-hint-${line.id}`}>{yh}</p> : null;
          })()}
        </div>

        {/* Quantity controls — splits mode OR single qty input */}
        {splitsActive ? (
          <div className="flex-1 max-w-[320px] shrink-0 space-y-1.5" data-testid={`count-splits-${line.id}`}>
            {splits.map((s, i) => (
              <div key={s._key || i} className="flex items-center gap-1.5">
                <Input
                  type="number"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  value={s.qty}
                  onChange={(e) => updateSplit(i, e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="w-16 h-10 text-center text-base font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder={`#${i + 1}`}
                  data-testid={`count-split-input-${line.id}-${i}`}
                />
                {/* iter374 — per-split unit override. Defaults to row's
                    primary unit; choosing another triggers conversion
                    via conversion_to_base when computing the row sum. */}
                {hasMultipleUnits && (
                  <select
                    value={s.unit_id ?? unitId ?? ''}
                    onChange={(e) => updateSplitUnit(i, e.target.value)}
                    className="h-10 px-1.5 text-xs border border-slate-200 rounded bg-white text-slate-700 min-w-[60px]"
                    data-testid={`count-split-unit-${line.id}-${i}`}
                  >
                    {units.map(u => (
                      <option key={u.id} value={u.id}>{u.unit_label}</option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => removeSplit(i)}
                  className="text-rose-400 hover:text-rose-700 active:scale-90 transition-transform p-1"
                  title="Remove this split"
                  data-testid={`count-split-remove-${line.id}-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between gap-2 pt-0.5">
              <span className="text-[11px] text-slate-500 tabular-nums" data-testid={`count-splits-sum-${line.id}`}>
                running: <span className="font-mono font-semibold text-slate-700">
                  {sumSplits(splits).toLocaleString('en-GB', { maximumFractionDigits: 2 })}
                </span> {unitLabel || ''}
              </span>
              <button
                type="button"
                onClick={initSplits}
                className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline px-2 py-1 touch-manipulation"
                data-testid={`count-split-add-${line.id}`}
              >
                + split
              </button>
            </div>
            {splitsNeedReview() && (
              <div
                className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-1"
                data-testid={`count-splits-review-${line.id}`}
              >
                ⚠ A split uses a measure without a defined conversion — flagged for auditor review.
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12 rounded-full touch-manipulation active:scale-90 transition-transform"
              onClick={handleDecrement}
              disabled={!isCounted || quantity <= 0}
              data-testid={`count-minus-btn-${line.id}`}
            >
              <Minus className="w-5 h-5" />
            </Button>

            <Input
              type="number"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onFocus={(e) => e.target.select()}
              className="w-20 h-12 text-center text-lg font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              placeholder="--"
              data-testid={`count-qty-input-${line.id}`}
            />

            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12 rounded-full touch-manipulation active:scale-90 transition-transform"
              onClick={handleIncrement}
              data-testid={`count-plus-btn-${line.id}`}
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>
        )}

        {/* Unit + expand */}
        <div className="flex items-center gap-1 shrink-0">
          {hasMultipleUnits ? (
            <select
              value={unitId || ''}
              onChange={handleUnitChange}
              className="h-12 px-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 min-w-[70px]"
              data-testid={`count-unit-select-${line.id}`}
            >
              {units.map(u => (
                <option key={u.id} value={u.id}>{u.unit_label}</option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-slate-500 min-w-[50px] text-center" data-testid={`count-unit-label-${line.id}`}>
              {unitLabel || units[0]?.unit_label || ''}
            </span>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className={`p-2.5 rounded-lg transition-colors touch-manipulation ${
              notes ? 'text-blue-500' : 'text-slate-300 hover:text-slate-500'
            }`}
            data-testid={`count-expand-btn-${line.id}`}
          >
            {expanded ? <ChevronUp className="w-5 h-5" /> : (
              notes ? <MessageSquare className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* iter365 — "+ split count" trigger (collapsed mode only). Lets
          the auditor break a single product's count into per-shelf /
          per-location splits. Hidden once splits are active because the
          splits UI has its own "+ split" affordance. */}
      {!splitsActive && (
        <div className="px-4 pb-2 -mt-2 flex justify-end">
          <button
            type="button"
            onClick={initSplits}
            className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline px-2 py-1 touch-manipulation"
            data-testid={`count-split-init-${line.id}`}
            title="Split this count across multiple shelves / locations"
          >
            + split count
          </button>
        </div>
      )}

      {/* Expanded notes section */}
      {expanded && (
        <div className="px-4 pb-3">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            placeholder="Add notes..."
            rows={2}
            className="w-full text-sm border border-slate-200 rounded-lg p-2.5 resize-none focus:ring-1 focus:ring-slate-300 focus:border-slate-300 touch-manipulation"
            data-testid={`count-notes-input-${line.id}`}
          />
        </div>
      )}
    </div>
  );
});
